import { maxDotNumber } from "@zyzgroup/core-common";
import { YZCanvasContext } from "../canvas";
import { type SVGParsedPathData } from "../svg";
import { L, LOptions } from "./L";

export type QuadraticBezierOptions = {
  cx: number;
  cy: number;
  toX?: number;
  toY?: number;
  isLineThrough?: boolean;
} & LOptions;

export class QuadraticBezier extends L {
  public cx: number;
  public cy: number;
  public isLineThrough: boolean;

  constructor(options: QuadraticBezierOptions) {
    super(options);
    this.cx = maxDotNumber(options.cx, 2);
    this.cy = maxDotNumber(options.cy, 2);
    this.isLineThrough =
      typeof options.isLineThrough == "boolean" ? options.isLineThrough : false;
  }

  /// Q cx,cy x,y
  /// T x,y
  public toSVGPathData(): SVGParsedPathData[] {
    if (this.isLineThrough) {
      const c1x = this.cx * 2 - (this.x + this.toX) / 2;
      const c1y = this.cy * 2 - (this.y + this.toY) / 2;
      return [["Q", c1x, c1y, this.toX, this.toY]];
    } else {
      return [["Q", this.cx, this.cy, this.toX, this.toY]];
    }
  }

  public toCanvasContext(ctx: YZCanvasContext) {
    if (this.isLineThrough) {
      const c1x = this.cx * 2 - (this.x + this.toX) / 2;
      const c1y = this.cy * 2 - (this.y + this.toY) / 2;
      ctx.quadraticCurveTo(c1x, c1y, this.toX, this.toY);
    } else {
      ctx.quadraticCurveTo(this.cx, this.cy, this.toX, this.toY);
    }
  }

  public getBoundingClientRect(e = 2) {
    const xArr = [this.x, this.toX];
    const yArr = [this.y, this.toY];
    let a, b, c, t;
    for (let i = 0; i < 2; ++i) {
      a =
        i == 0
          ? this.x - 2 * this.cx + this.toX
          : this.y - 2 * this.cy + this.toY;
      b = i == 0 ? -2 * this.x + 2 * this.cx : -2 * this.y + 2 * this.cy;
      c = i == 0 ? this.x : this.y;
      if (Math.abs(a) > 1e-12) {
        t = -b / (2 * a);
        if (0 < t && t < 1) {
          [xArr, yArr][i].push(a * t * t + b * t + c);
        }
      }
    }
    const min = { x: Math.min(0, ...xArr), y: Math.min(0, ...yArr) };
    const max = { x: Math.max(0, ...xArr), y: Math.max(0, ...yArr) };
    return {
      x: maxDotNumber(min.x, e),
      y: maxDotNumber(min.y, e),
      width: maxDotNumber(max.x - min.x, e),
      height: maxDotNumber(max.y - min.y, e)
    };
  }

  // t (0.0 to 1.0) : The fraction of the overall duration of the path
  public interpolate(t: number): { x: number; y: number } {
    return {
      x:
        (1 - t) * (1 - t) * this.x +
        2 * (1 - t) * t * this.cx +
        t * t * this.toX,
      y:
        (1 - t) * (1 - t) * this.y +
        2 * (1 - t) * t * this.cy +
        t * t * this.toY
    };
  }
}

export default QuadraticBezier;
